/**
 * Per-event localStorage for the public activity-signup form. Lets anonymous
 * visitors skip re-typing their name/email/phone on every activity signup
 * within the same event. Server still re-validates on submit — this is UX
 * prefill only, not a trust boundary.
 */

const STORAGE_KEY_PREFIX = "lp_attendee_prefill_";

export interface AttendeePrefill {
  firstName: string;
  lastName: string;
  email: string;
  phoneDigits: string;
  countryDialCodeId: number | null;
}

/**
 * Loads previously-saved signup identity for the given event, or null when
 * nothing is saved / the saved value is malformed / localStorage is unavailable.
 *
 * @param eventId - The event scope to look up.
 * @returns The saved prefill payload, or null.
 */
export function loadAttendeePrefill(eventId: number): AttendeePrefill | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${eventId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isAttendeePrefill(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persists signup identity for the given event so the next activity signup
 * can prefill the form. Silently no-ops when localStorage is unavailable
 * (private browsing, storage quota, SSR).
 *
 * @param eventId - The event scope to save under.
 * @param info - The identity fields to remember.
 */
export function saveAttendeePrefill(eventId: number, info: AttendeePrefill): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${eventId}`, JSON.stringify(info));
  } catch {
    // localStorage may be disabled or full; the form still works without prefill.
  }
}

function isAttendeePrefill(value: unknown): value is AttendeePrefill {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.firstName === "string" &&
    typeof v.lastName === "string" &&
    typeof v.email === "string" &&
    typeof v.phoneDigits === "string" &&
    (v.countryDialCodeId === null || typeof v.countryDialCodeId === "number")
  );
}
